import { logger } from "@/lib/logger";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Sparkles, FileText, Mail, Share2, Megaphone, Layout,
  Plus, Loader2, LogOut, CheckCircle, Clock, Eye, Send,
  Sliders, Users, ChevronDown, Pencil, Trash2, X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/content`;

const CHANNELS = {
  blog: { name: "Blog Post", icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10" },
  email: { name: "Email Campaign", icon: Mail, color: "text-amber-400", bg: "bg-amber-500/10" },
  social: { name: "Social Media", icon: Share2, color: "text-pink-400", bg: "bg-pink-500/10" },
  ads: { name: "Ad Copy", icon: Megaphone, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  landing_page: { name: "Landing Page", icon: Layout, color: "text-violet-400", bg: "bg-violet-500/10" },
};

const STATUS_COLORS = {
  drafted: "bg-zinc-500/10 text-zinc-400",
  in_review: "bg-amber-500/10 text-amber-400",
  approved: "bg-emerald-500/10 text-emerald-400",
  published: "bg-blue-500/10 text-blue-400",
};

function ToneSlider({ label, value, onChange, leftLabel, rightLabel }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-zinc-400">
        <span>{leftLabel}</span>
        <span className="text-zinc-400 font-medium">{label}</span>
        <span>{rightLabel}</span>
      </div>
      <input type="range" min="0" max="1" step="0.1" value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-violet-500" />
    </div>
  );
}

// ==================== LOGIN ====================
function ContentLogin({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "auth/login" : "auth/signup";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const resp = await fetch(`${API}/${endpoint}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (resp.ok) {
        onAuth(data);
      } else {
        toast.error(data.detail || "Authentication failed");
      }
    } catch (e) {
      toast.error("Connection error. Please try again.");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-violet-400" />
          </div>
          <h1 className="font-heading text-3xl text-white font-bold">Content AI Agent</h1>
          <p className="text-zinc-400 mt-2">Multi-channel AI content marketing</p>
        </div>

        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-2 mb-4">
              <Button onClick={() => setMode("login")} variant={mode === "login" ? "default" : "ghost"} className={mode === "login" ? "flex-1 bg-white text-black" : "flex-1 text-zinc-400"}>Sign In</Button>
              <Button onClick={() => setMode("signup")} variant={mode === "signup" ? "default" : "ghost"} className={mode === "signup" ? "flex-1 bg-white text-black" : "flex-1 text-zinc-400"}>Sign Up</Button>
            </div>
            {mode === "signup" && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-black/50 border-white/10 text-white" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="bg-black/50 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="bg-black/50 border-white/10 text-white"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()} />
            </div>
            <Button onClick={handleSubmit} disabled={loading} className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </CardContent>
        </Card>
        <p className="text-zinc-500 text-xs text-center mt-4">Powered by ROAS-AI</p>
      </motion.div>
    </div>
  );
}

// ==================== PENDING APPROVAL ====================
function PendingApproval({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-white text-xl font-bold mb-2">Account Pending Approval</h2>
        <p className="text-zinc-400 mb-6">Your account ({user.email}) is awaiting admin approval. You'll receive access once approved.</p>
        <Button onClick={onLogout} variant="outline" className="border-white/20 text-white">
          <LogOut className="w-4 h-4 mr-2" /> Log Out
        </Button>
      </div>
    </div>
  );
}

// ==================== ADMIN PANEL ====================
function AdminPanel({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (resp.ok) setUsers(await resp.json());
    } catch (e) { logger.error("Error:", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAction = async (userId, action) => {
    try {
      const resp = await fetch(`${API}/admin/users/${userId}/${action}`, { method: "POST", credentials: "include" });
      if (resp.ok) { toast.success(`User ${action}d`); fetchUsers(); }
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-white text-lg font-bold flex items-center gap-2"><Users className="w-5 h-5 text-violet-400" /> User Management</h2>
        <Button onClick={onBack} variant="ghost" className="text-zinc-400">Back to Dashboard</Button>
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-zinc-400 animate-spin mx-auto" /> : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between p-4 bg-[#121212] border border-white/10 rounded-xl">
              <div>
                <p className="text-white font-medium">{u.name}</p>
                <p className="text-zinc-400 text-sm">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={u.approved === "Y" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}>
                  {u.approved === "Y" ? "Approved" : "Pending"}
                </Badge>
                {u.approved !== "Y" && (
                  <Button size="sm" onClick={() => handleAction(u.user_id, "approve")} className="bg-emerald-600 text-white text-xs">Approve</Button>
                )}
                {u.approved === "Y" && u.email !== "matgeo.2008@gmail.com" && (
                  <Button size="sm" variant="outline" onClick={() => handleAction(u.user_id, "reject")} className="border-red-500/30 text-red-400 text-xs">Revoke</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN DASHBOARD ====================
function ContentDashboard({ user, onLogout, onSwitchView }) {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showEditor, setShowEditor] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [genForm, setGenForm] = useState({
    channel: "blog", topic: "", brand_id: "",
    tone_technical: 0.5, tone_corporate: 0.5, tone_analytical: 0.5,
    additional_instructions: "",
  });

  const fetchItems = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/items`, { credentials: "include" });
      if (resp.ok) setItems(await resp.json());
    } catch (e) { logger.error("Error:", e); }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/brands`, { credentials: "include" });
      if (resp.ok) setBrands(await resp.json());
    } catch (e) { logger.error("Error:", e); }
  }, []);

  useEffect(() => { fetchItems(); fetchBrands(); }, [fetchItems, fetchBrands]);

  const handleGenerate = async () => {
    if (!genForm.topic) { toast.error("Topic is required"); return; }
    setGenerating(true);
    try {
      const resp = await fetch(`${API}/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(genForm),
      });
      if (resp.ok) {
        toast.success("Content generated!");
        setShowGenerate(false);
        setGenForm({ ...genForm, topic: "", additional_instructions: "" });
        fetchItems();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Generation failed");
      }
    } catch { toast.error("Generation failed"); }
    finally { setGenerating(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      const resp = await fetch(`${API}/items/${id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (resp.ok) { fetchItems(); toast.success(`Status: ${status.replace("_", " ")}`); }
    } catch (e) { logger.error("Error:", e); }
  };

  const deleteItem = async (id) => {
    try {
      await fetch(`${API}/items/${id}`, { method: "DELETE", credentials: "include" });
      fetchItems();
    } catch (e) { logger.error("Error:", e); }
  };

  const filteredItems = filter === "all" ? items : items.filter((i) => i.channel === filter);
  const isAdmin = user.role === "admin" || user.email === "matgeo.2008@gmail.com";

  if (showAdmin) return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-4xl mx-auto"><AdminPanel onBack={() => setShowAdmin(false)} /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <span className="font-heading text-lg text-white font-bold">Content AI</span>
          </div>
          <div className="flex items-center gap-3">
            {onSwitchView && <Button size="sm" variant="ghost" onClick={onSwitchView} className="text-zinc-400 font-mono text-xs">&gt;_ Terminal</Button>}
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => setShowAdmin(true)} className="text-zinc-400"><Users className="w-4 h-4 mr-1" /> Admin</Button>}
            <span className="text-zinc-400 text-sm">{user.email}</span>
            <Button size="sm" variant="ghost" onClick={onLogout} className="text-zinc-400"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-white text-2xl font-bold">Content Dashboard</h1>
            <p className="text-zinc-400 text-sm">{items.length} pieces of content</p>
          </div>
          <Button onClick={() => setShowGenerate(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Generate Content
          </Button>
        </div>

        {/* Channel Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button size="sm" variant={filter === "all" ? "default" : "ghost"} onClick={() => setFilter("all")} className={filter === "all" ? "bg-white text-black" : "text-zinc-400"}>All</Button>
          {Object.entries(CHANNELS).map(([key, ch]) => {
            const Icon = ch.icon;
            const count = items.filter((i) => i.channel === key).length;
            return (
              <Button key={key} size="sm" variant={filter === key ? "default" : "ghost"} onClick={() => setFilter(key)}
                className={filter === key ? "bg-white text-black" : "text-zinc-400"}>
                <Icon className="w-3 h-3 mr-1" /> {ch.name} {count > 0 && <Badge className="ml-1 bg-white/20 text-xs">{count}</Badge>}
              </Button>
            );
          })}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {filteredItems.map((item) => {
              const ch = CHANNELS[item.channel] || CHANNELS.blog;
              const Icon = ch.icon;
              return (
                <motion.div key={item.content_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="bg-[#121212] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", ch.bg)}>
                        <Icon className={cn("w-4 h-4", ch.color)} />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium truncate max-w-[180px]">{item.topic}</p>
                        <p className="text-zinc-500 text-xs">{ch.name}</p>
                      </div>
                    </div>
                    <Badge className={cn("text-xs", STATUS_COLORS[item.status])}>{item.status.replace("_", " ")}</Badge>
                  </div>
                  <p className="text-zinc-400 text-xs line-clamp-3 mb-3">{item.content?.slice(0, 150)}...</p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500 text-xs">{new Date(item.created_at).toLocaleDateString()}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setShowEditor(item)} className="text-zinc-400 p-1 h-7"><Eye className="w-3 h-3" /></Button>
                      {item.status === "drafted" && <Button size="sm" variant="ghost" onClick={() => updateStatus(item.content_id, "in_review")} className="text-amber-400 p-1 h-7"><Send className="w-3 h-3" /></Button>}
                      {item.status === "in_review" && <Button size="sm" variant="ghost" onClick={() => updateStatus(item.content_id, "approved")} className="text-emerald-400 p-1 h-7"><CheckCircle className="w-3 h-3" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => deleteItem(item.content_id)} className="text-zinc-500 hover:text-red-400 p-1 h-7"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-16">
            <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">No content yet. Click "Generate Content" to start.</p>
          </div>
        )}
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-400" /> Generate Content</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Channel</Label>
              <Select value={genForm.channel} onValueChange={(v) => setGenForm({ ...genForm, channel: v })}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">
                  {Object.entries(CHANNELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Topic / Brief</Label>
              <Textarea value={genForm.topic} onChange={(e) => setGenForm({ ...genForm, topic: e.target.value })} placeholder="What should this content be about?" className="bg-black/50 border-white/10 text-white min-h-[80px]" />
            </div>
            {brands.length > 0 && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Brand Profile</Label>
                <Select value={genForm.brand_id} onValueChange={(v) => setGenForm({ ...genForm, brand_id: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white"><SelectValue placeholder="Select brand (optional)" /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    <SelectItem value="" className="text-white">No brand</SelectItem>
                    {brands.map((b) => <SelectItem key={b.brand_id} value={b.brand_id} className="text-white">{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-3 p-3 bg-white/5 rounded-xl">
              <p className="text-zinc-400 text-xs font-medium flex items-center gap-1"><Sliders className="w-3 h-3" /> Tone Adjusters</p>
              <ToneSlider label="Technical Level" value={genForm.tone_technical} onChange={(v) => setGenForm({ ...genForm, tone_technical: v })} leftLabel="Conversational" rightLabel="Technical" />
              <ToneSlider label="Formality" value={genForm.tone_corporate} onChange={(v) => setGenForm({ ...genForm, tone_corporate: v })} leftLabel="Casual" rightLabel="Corporate" />
              <ToneSlider label="Style" value={genForm.tone_analytical} onChange={(v) => setGenForm({ ...genForm, tone_analytical: v })} leftLabel="Bold" rightLabel="Analytical" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Additional Instructions (optional)</Label>
              <Input value={genForm.additional_instructions} onChange={(e) => setGenForm({ ...genForm, additional_instructions: e.target.value })} placeholder="Any specific requirements..." className="bg-black/50 border-white/10 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowGenerate(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={!!showEditor} onOpenChange={() => setShowEditor(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          {showEditor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => { const Icon = CHANNELS[showEditor.channel]?.icon || FileText; return <Icon className="w-5 h-5 text-violet-400" />; })()}
                  {showEditor.topic}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={cn("text-xs", STATUS_COLORS[showEditor.status])}>{showEditor.status.replace("_", " ")}</Badge>
                <span className="text-zinc-500 text-xs">{CHANNELS[showEditor.channel]?.name}</span>
                <span className="text-zinc-500 text-xs">{new Date(showEditor.created_at).toLocaleDateString()}</span>
              </div>
              <div className="prose prose-invert prose-sm max-w-none bg-[#121212] border border-white/10 rounded-xl p-6">
                <ReactMarkdown>{showEditor.content}</ReactMarkdown>
              </div>
              <DialogFooter className="flex gap-2">
                {showEditor.status === "drafted" && <Button onClick={() => { updateStatus(showEditor.content_id, "in_review"); setShowEditor(null); }} className="bg-amber-600 text-white">Submit for Review</Button>}
                {showEditor.status === "in_review" && <Button onClick={() => { updateStatus(showEditor.content_id, "approved"); setShowEditor(null); }} className="bg-emerald-600 text-white">Approve</Button>}
                {showEditor.status === "approved" && <Button onClick={() => { updateStatus(showEditor.content_id, "published"); setShowEditor(null); }} className="bg-blue-600 text-white">Mark Published</Button>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== TERMINAL ====================
function ContentTerminal({ user, onLogout, onSwitchView }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [processing, setProcessing] = useState(false);
  const termRef = useRef(null);
  const inputRef = useRef(null);

  const addOutput = useCallback((type, text) => {
    setHistory((h) => [...h, { type, text, ts: Date.now() }]);
  }, []);

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    addOutput("system", `Content AI Agent v1.0 — Logged in as ${user.email}`);
    addOutput("system", "Type 'help' for available commands.\n");
  }, [user.email, addOutput]);

  useEffect(() => {
    termRef.current?.scrollTo(0, termRef.current.scrollHeight);
  }, [history]);

  const handleCommand = async (cmd) => {
    const raw = cmd.trim();
    if (!raw) return;
    addOutput("input", raw);
    setInput("");

    const parts = raw.split(/\s+/);
    const command = parts[0].toLowerCase();
    const args = parts.slice(1).join(" ");

    setProcessing(true);

    try {
      switch (command) {
        case "help":
          addOutput("system", [
            "Available commands:",
            "",
            "  generate <channel> <topic>  — Generate content",
            "    Channels: blog, email, social, ads, landing_page",
            "    Example: generate blog How AI transforms advertising",
            "",
            "  tone <technical> <corporate> <analytical>",
            "    Set tone sliders (0.0 to 1.0). Example: tone 0.8 0.3 0.5",
            "",
            "  list [channel] [status]     — List content items",
            "    Example: list blog drafted",
            "",
            "  view <content_id>           — View content item",
            "  approve <content_id>        — Approve content",
            "  publish <content_id>        — Mark as published",
            "  delete <content_id>         — Delete content",
            "",
            "  brands                      — List brand profiles",
            "  brand create <name>         — Create a brand",
            "",
            "  users                       — List users (admin)",
            "  approve-user <user_id>      — Approve user (admin)",
            "",
            "  dashboard                   — Switch to GUI dashboard",
            "  clear                       — Clear terminal",
            "  logout                      — Log out",
            "",
          ].join("\n"));
          break;

        case "clear":
          setHistory([]);
          break;

        case "logout":
          onLogout();
          break;

        case "dashboard":
          onSwitchView();
          break;

        case "tone":
          if (args) {
            const vals = args.split(/\s+/).map(Number);
            if (vals.length >= 3 && vals.every(v => v >= 0 && v <= 1)) {
              toneRef.current = { technical: vals[0], corporate: vals[1], analytical: vals[2] };
              addOutput("success", `Tone set: Technical=${vals[0]} Corporate=${vals[1]} Analytical=${vals[2]}`);
            } else {
              addOutput("error", "Usage: tone <technical 0-1> <corporate 0-1> <analytical 0-1>");
            }
          } else {
            const t = toneRef.current;
            addOutput("info", `Current tone: Technical=${t.technical} Corporate=${t.corporate} Analytical=${t.analytical}`);
          }
          break;

        case "generate": {
          const spaceIdx = args.indexOf(" ");
          if (spaceIdx === -1) { addOutput("error", "Usage: generate <channel> <topic>"); break; }
          const channel = args.substring(0, spaceIdx);
          const topic = args.substring(spaceIdx + 1);
          if (!["blog", "email", "social", "ads", "landing_page"].includes(channel)) {
            addOutput("error", "Invalid channel. Use: blog, email, social, ads, landing_page");
            break;
          }
          addOutput("info", `Generating ${channel} content...`);
          const t = toneRef.current;
          const resp = await fetch(`${API}/generate`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ channel, topic, tone_technical: t.technical, tone_corporate: t.corporate, tone_analytical: t.analytical }),
          });
          if (resp.ok) {
            const data = await resp.json();
            addOutput("success", `Generated: ${data.content_id} (${data.channel_name})`);
            addOutput("content", data.content);
          } else {
            const err = await resp.json();
            addOutput("error", err.detail || "Generation failed");
          }
          break;
        }

        case "list": {
          const listParts = args.split(/\s+/).filter(Boolean);
          let url = `${API}/items`;
          const params = new URLSearchParams();
          if (listParts[0]) params.set("channel", listParts[0]);
          if (listParts[1]) params.set("status", listParts[1]);
          if (params.toString()) url += `?${params}`;
          const resp = await fetch(url, { credentials: "include" });
          if (resp.ok) {
            const items = await resp.json();
            if (items.length === 0) { addOutput("info", "No content items found."); break; }
            const lines = items.map(i =>
              `  ${i.content_id}  ${i.channel.padEnd(13)} ${i.status.padEnd(12)} ${i.topic.substring(0, 50)}`
            );
            addOutput("info", `${items.length} items:\n\n  ${"ID".padEnd(18)} ${"Channel".padEnd(13)} ${"Status".padEnd(12)} Topic\n${lines.join("\n")}`);
          }
          break;
        }

        case "view": {
          if (!args) { addOutput("error", "Usage: view <content_id>"); break; }
          const resp = await fetch(`${API}/items`, { credentials: "include" });
          if (resp.ok) {
            const items = await resp.json();
            const item = items.find(i => i.content_id === args);
            if (item) {
              addOutput("info", `[${item.channel_name}] ${item.topic} — ${item.status}`);
              addOutput("content", item.content);
            } else {
              addOutput("error", "Content not found");
            }
          }
          break;
        }

        case "approve": {
          if (!args) { addOutput("error", "Usage: approve <content_id>"); break; }
          const resp = await fetch(`${API}/items/${args}`, {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "approved" }),
          });
          addOutput(resp.ok ? "success" : "error", resp.ok ? `${args} approved` : "Failed");
          break;
        }

        case "publish": {
          if (!args) { addOutput("error", "Usage: publish <content_id>"); break; }
          const resp = await fetch(`${API}/items/${args}`, {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "published" }),
          });
          addOutput(resp.ok ? "success" : "error", resp.ok ? `${args} published` : "Failed");
          break;
        }

        case "delete": {
          if (!args) { addOutput("error", "Usage: delete <content_id>"); break; }
          const resp = await fetch(`${API}/items/${args}`, { method: "DELETE", credentials: "include" });
          addOutput(resp.ok ? "success" : "error", resp.ok ? `${args} deleted` : "Failed");
          break;
        }

        case "brands": {
          const resp = await fetch(`${API}/brands`, { credentials: "include" });
          if (resp.ok) {
            const brands = await resp.json();
            if (brands.length === 0) { addOutput("info", "No brands. Use: brand create <name>"); break; }
            addOutput("info", brands.map(b => `  ${b.brand_id}  ${b.name}  (${b.industry || "no industry"})`).join("\n"));
          }
          break;
        }

        case "brand": {
          if (args.startsWith("create ")) {
            const name = args.substring(7);
            const t = toneRef.current;
            const resp = await fetch(`${API}/brands`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ name, tone_technical: t.technical, tone_corporate: t.corporate, tone_analytical: t.analytical }),
            });
            if (resp.ok) {
              const b = await resp.json();
              addOutput("success", `Brand created: ${b.brand_id} (${b.name})`);
            }
          } else {
            addOutput("error", "Usage: brand create <name>");
          }
          break;
        }

        case "users": {
          const resp = await fetch(`${API}/admin/users`, { credentials: "include" });
          if (resp.ok) {
            const users = await resp.json();
            addOutput("info", users.map(u => `  ${u.user_id}  ${u.email.padEnd(30)} ${u.approved === "Y" ? "APPROVED" : "PENDING"}`).join("\n"));
          } else {
            addOutput("error", "Admin access required");
          }
          break;
        }

        case "approve-user": {
          if (!args) { addOutput("error", "Usage: approve-user <user_id>"); break; }
          const resp = await fetch(`${API}/admin/users/${args}/approve`, { method: "POST", credentials: "include" });
          addOutput(resp.ok ? "success" : "error", resp.ok ? `User ${args} approved` : "Failed");
          break;
        }

        default:
          addOutput("error", `Unknown command: ${command}. Type 'help' for available commands.`);
      }
    } catch (e) {
      addOutput("error", `Error: ${e.message || "Command failed"}`);
    }

    setProcessing(false);
    inputRef.current?.focus();
  };

  const toneRef = useRef({ technical: 0.5, corporate: 0.5, analytical: 0.5 });

  const lineColors = {
    input: "text-emerald-400",
    system: "text-violet-400",
    info: "text-zinc-300",
    success: "text-emerald-400",
    error: "text-red-400",
    content: "text-zinc-400",
  };

  return (
    <div className="min-h-screen bg-black flex flex-col font-mono">
      {/* Terminal Header */}
      <div className="bg-[#1a1a1a] border-b border-white/10 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
          </div>
          <span className="text-zinc-400 text-sm">content-ai-agent — {user.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onSwitchView} className="text-zinc-400 text-xs hover:text-zinc-300 transition-colors">GUI Mode</button>
          <button onClick={onLogout} className="text-zinc-400 text-xs hover:text-red-400 transition-colors">Exit</button>
        </div>
      </div>

      {/* Terminal Body */}
      <div ref={termRef} className="flex-1 overflow-y-auto p-4 space-y-1 text-sm" onClick={() => inputRef.current?.focus()}>
        {history.map((line, i) => (
          <div key={`${line.ts}-${i}`} className={cn("whitespace-pre-wrap", lineColors[line.type] || "text-zinc-400")}>
            {line.type === "input" ? (
              <span><span className="text-violet-500">$</span> {line.text}</span>
            ) : line.type === "content" ? (
              <div className="ml-2 pl-3 border-l border-white/10 my-2 text-zinc-400 text-xs leading-relaxed max-h-[400px] overflow-y-auto">
                {line.text}
              </div>
            ) : (
              line.text
            )}
          </div>
        ))}
        {processing && <div className="text-violet-400 animate-pulse">Processing...</div>}

        {/* Input Line */}
        <div className="flex items-center gap-2">
          <span className="text-violet-500">$</span>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !processing && handleCommand(input)}
            className="flex-1 bg-transparent text-emerald-400 outline-none caret-emerald-400"
            autoFocus
            disabled={processing}
          />
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN APP ====================
export default function NewAgentsPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [viewMode, setViewMode] = useState("dashboard"); // "terminal" or "dashboard"

  const checkAuth = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (resp.ok) setUser(await resp.json());
    } catch (e) { logger.error("Error:", e); } finally { setChecking(false); }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  if (checking) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
    </div>
  );

  if (!user) return <ContentLogin onAuth={(u) => setUser(u)} />;
  if (user.approved !== "Y") return <PendingApproval user={user} onLogout={handleLogout} />;
  if (viewMode === "terminal") return <ContentTerminal user={user} onLogout={handleLogout} onSwitchView={() => setViewMode("dashboard")} />;
  return <ContentDashboard user={user} onLogout={handleLogout} onSwitchView={() => setViewMode("terminal")} />;
}
